'use client';

import { useCallback, useLayoutEffect, useRef, useState, type RefObject } from 'react';

/**
 * Whether an element's text is cut: taller (a line clamp) or wider (an unbreakable run) than its
 * box. One pixel of slack absorbs sub-pixel rounding. An element with no box at all (no layout:
 * jsdom, or a `display: none` ancestor) cannot be measured and counts as `whenUnmeasurable`, which
 * is clamped by default: that keeps a tooltip on, as it always was. A control that appears only
 * when text is cut (the description's "More" toggle) passes `false`, so nothing renders for text
 * nobody can measure.
 */
export function isClamped(el: HTMLElement, whenUnmeasurable = true): boolean {
  if (el.clientHeight === 0 && el.clientWidth === 0) return whenUnmeasurable;
  return el.scrollHeight > el.clientHeight + 1 || el.scrollWidth > el.clientWidth + 1;
}

/**
 * Measured, never guessed from a character count: re-measures before paint, when `key` changes
 * (pass the text or node being measured) and whenever the element's box is resized. The one
 * measurement behind the overflow-aware TruncatedText tooltip and the page header's clamped title
 * (page header contract H5, #15269 S6).
 *
 * Reports clamped until measured and wherever nothing can be measured, unless
 * `options.whenUnmeasurable` is `false`, which makes both read "fits".
 *
 * The observer is built once per ELEMENT: `key` may be a new object every render (a JSX title), and
 * that only re-runs the cheap measure. If React replaces the element (moving the measured node under
 * a new parent mounts a new one, as the page header's nav icon appearing does), the observer follows
 * it; one left on the detached old element would never fire again.
 */
export function useIsClamped(
  ref: RefObject<HTMLElement | null>,
  key: unknown,
  { whenUnmeasurable = true }: { whenUnmeasurable?: boolean } = {},
): boolean {
  const [clamped, setClamped] = useState(whenUnmeasurable);
  const measure = useCallback(() => {
    const el = ref.current;
    if (el) setClamped(isClamped(el, whenUnmeasurable));
  }, [ref, whenUnmeasurable]);
  // Re-measure when what is being measured changes (and once on mount, before paint).
  useLayoutEffect(() => {
    measure();
  }, [measure, key]);
  // Re-measure when the box is resized. The observer is per element, not per render: after each
  // render this only checks that it is still watching the element the ref points at.
  const measureRef = useRef(measure);
  const observed = useRef<{ el: HTMLElement; observer: ResizeObserver } | null>(null);
  useLayoutEffect(() => {
    measureRef.current = measure;
    const el = ref.current;
    if (observed.current?.el === el) return;
    observed.current?.observer.disconnect();
    observed.current = null;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => measureRef.current());
    observer.observe(el);
    observed.current = { el, observer };
    measureRef.current(); // a new element: measure it now, whatever the key did
  });
  useLayoutEffect(
    () => () => {
      observed.current?.observer.disconnect();
      observed.current = null;
    },
    [],
  );
  return clamped;
}
